import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Formik, Form, Field, ErrorMessage as FormikErrorMessage } from 'formik';
import * as Yup from 'yup';
import Button from '../common/Button';
import { adminService } from '../../services/api';
import { LANGUAGES, languagesForRole } from '../../i18n/languages';

const FormContainer = styled.div`
  padding: ${({ theme }) => theme.spacing[2]};
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[4]};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const Input = styled(Field)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}15;
  }
`;

const TextArea = styled(Field)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}15;
  }
`;

const Select = styled(Field)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}15;
  }
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[6]};
`;

const Hint = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const SuccessHint = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.success};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

// CVR input + "Fetch from CVR" button side by side (feedback #4)
const CvrRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const AssignedEmployeesSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const EmployeeList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const EmployeeCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[2]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  &:hover {
    background-color: ${({ theme, $disabled }) => ($disabled ? 'transparent' : theme.colors.backgroundLight)};
  }

  input {
    margin-right: ${({ theme }) => theme.spacing[2]};
  }
`;

// Form validation schema
const customerSchema = Yup.object().shape({
  name: Yup.string().required('Company name is required'),
  contactEmail: Yup.string().email('Invalid email address').required('Email is required'),
  // contactPhone, address and industry are nullable/optional in the backend and
  // importer, so keep them optional here to allow editing legacy customers that
  // lack these fields.
  contactPhone: Yup.string(),
  address: Yup.string(),
  industry: Yup.string(),
  // cvr is the business CustomerID (Danish CVR / e-conomic corporate id). Optional,
  // but must be exactly 8 digits when provided. excludeEmptyString keeps it optional.
  cvr: Yup.string().matches(/^\d{8}$/, { message: 'CVR must be 8 digits', excludeEmptyString: true }),
  // invoiceEmail is the billing / e-conomic invoice recipient, distinct from contactEmail.
  invoiceEmail: Yup.string().email('Invalid email address'),
  approvalReminderFrequency: Yup.string().oneOf(['weekly', 'off']).required(),
});

const CustomerForm = ({ customer, onSubmit, onCancel, isEditing, localActions = false }) => {
  const [employees, setEmployees] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  // CVR register lookup (feedback #4): {loading, error, companyName}.
  const [cvrLookup, setCvrLookup] = useState({ loading: false, error: null, companyName: null });
  // Save errors thrown by onSubmit (e.g. 409 EMPLOYEE_ALREADY_ASSIGNED /
  // duplicate e-mail) are shown inline instead of being swallowed.
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch active employees WITH their customer assignments (one-customer
        // rule, feedback #16) — request all rows so the assignment picker isn't
        // capped at the backend default page size.
        const employeesResponse = await adminService.getEmployees({
          isActive: true,
          limit: 1000,
          page: 1
        });

        // Fetch industry options
        const industriesResponse = await adminService.getIndustryOptions();

        setEmployees(employeesResponse.employees || []);
        setIndustries(industriesResponse || []);
      } catch (error) {
        console.error('Error fetching form data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const initialValues = {
    name: customer?.name || '',
    contactEmail: customer?.contactEmail || '',
    contactPhone: customer?.contactPhone || '',
    address: customer?.address || '',
    industry: customer?.industry || '',
    cvr: customer?.cvr || '',
    invoiceEmail: customer?.invoiceEmail || '',
    approvalReminderFrequency: customer?.approvalReminderFrequency || 'weekly',
    // Preferred UI language (da/de/en, feedback #3) — '' = browser default.
    language: customer?.language || '',
    assignedEmployees: customer?.assignedEmployees || []
  };

  // Formik resets isSubmitting when this async handler resolves; catching here
  // keeps the dialog open with the backend error message shown inline.
  const handleSubmit = async (values) => {
    try {
      setSubmitError(null);
      await onSubmit(values);
    } catch (error) {
      setSubmitError(error.message || 'Failed to save the customer. Please try again.');
    }
  };

  // Official CVR register lookup — overwrites company name + address with the
  // registered data (the admin can still edit both afterwards).
  const handleCvrLookup = async (cvr, setFieldValue) => {
    try {
      setCvrLookup({ loading: true, error: null, companyName: null });
      const { company } = await adminService.lookupCvr(cvr);
      setFieldValue('name', company.name || '');
      setFieldValue('address', company.address || '');
      setCvrLookup({ loading: false, error: null, companyName: company.name });
    } catch (error) {
      setCvrLookup({ loading: false, error: error.message || 'CVR lookup failed. Please try again.', companyName: null });
    }
  };

  if (loading) {
    return <div>Loading form data...</div>;
  }

  return (
    <FormContainer>
      <Formik
        initialValues={initialValues}
        validationSchema={customerSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form>
            <FormGroup>
              <Label htmlFor="name">Company Name</Label>
              <Input type="text" id="name" name="name" />
              <FormikErrorMessage name="name" component={ErrorMessage} />
            </FormGroup>
            
            <FormRow>
              <FormGroup>
                <Label htmlFor="contactEmail">Login / Contact Email</Label>
                <Input type="email" id="contactEmail" name="contactEmail" />
                {isEditing && (
                  <Hint>
                    Changing this signs the customer out and invalidates the previous login link. Send a new invitation after saving.
                  </Hint>
                )}
                <FormikErrorMessage name="contactEmail" component={ErrorMessage} />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="contactPhone">Contact Phone</Label>
                <Input type="text" id="contactPhone" name="contactPhone" />
                <FormikErrorMessage name="contactPhone" component={ErrorMessage} />
              </FormGroup>
            </FormRow>
            
            <FormGroup>
              <Label htmlFor="address">Address</Label>
              <TextArea forwardedAs="textarea" id="address" name="address" />
              <FormikErrorMessage name="address" component={ErrorMessage} />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="industry">Industry</Label>
              <Select forwardedAs="select" id="industry" name="industry">
                <option value="">Select Industry</option>
                {industries.map(industry => (
                  <option key={industry} value={industry}>
                    {industry}
                  </option>
                ))}
              </Select>
              <FormikErrorMessage name="industry" component={ErrorMessage} />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label htmlFor="cvr">CVR</Label>
                <CvrRow>
                  <Input type="text" id="cvr" name="cvr" />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    style={{ whiteSpace: 'nowrap' }}
                    disabled={cvrLookup.loading || !/^\d{8}$/.test(values.cvr || '')}
                    onClick={() => handleCvrLookup(values.cvr, setFieldValue)}
                  >
                    {cvrLookup.loading ? 'Fetching…' : 'Fetch from CVR'}
                  </Button>
                </CvrRow>
                <Hint>Danish CVR number — 8 digits (business ID)</Hint>
                {cvrLookup.companyName && (
                  <SuccessHint>
                    Found "{cvrLookup.companyName}" in the CVR register — company name and address filled in.
                  </SuccessHint>
                )}
                {cvrLookup.error && <ErrorMessage>{cvrLookup.error}</ErrorMessage>}
                <FormikErrorMessage name="cvr" component={ErrorMessage} />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="invoiceEmail">Invoice Email</Label>
                <Input type="email" id="invoiceEmail" name="invoiceEmail" />
                <Hint>Billing email for invoices (may differ from contact email)</Hint>
                <FormikErrorMessage name="invoiceEmail" component={ErrorMessage} />
              </FormGroup>
            </FormRow>

            {/* Preferred UI language — sets the customer's app language on login (feedback #3) */}
            <FormGroup>
              <Label htmlFor="language">Preferred language</Label>
              <Select forwardedAs="select" id="language" name="language">
                <option value="">(Use browser default)</option>
                {languagesForRole('customer').map((code) => (
                  <option key={code} value={code}>{LANGUAGES[code] ? LANGUAGES[code].nativeName : code}</option>
                ))}
              </Select>
              <Hint>The customer sees the app in this language when they log in.</Hint>
            </FormGroup>

            <AssignedEmployeesSection>
              <FormGroup>
                <Label htmlFor="approvalReminderFrequency">Customer approval email reminders</Label>
                <Select forwardedAs="select" id="approvalReminderFrequency" name="approvalReminderFrequency">
                  <option value="weekly">Mondays at 06:00, when approval is required</option>
                  <option value="off">Off</option>
                </Select>
                <Hint>
                  Every Monday at 06:00 Danish local time (CET/CEST), sends one email for finished hours awaiting approval, including recently submitted hours. Invitations and login links requested by the customer are separate.
                </Hint>
                {customer?.approvalReminderSchedule && (
                  <Hint role="status">Currently effective: {customer.approvalReminderSchedule.description}.</Hint>
                )}
              </FormGroup>
              {localActions && <>
              <SectionTitle>Assigned Employees</SectionTitle>
              <Hint>Select employees who will work with this customer</Hint>
              
              <EmployeeList>
                {employees.length > 0 ? (
                  employees.map(employee => {
                    // One-customer rule (feedback #16): an employee already
                    // assigned to a DIFFERENT customer cannot be added here.
                    // Already-checked rows stay removable.
                    const conflict = (employee.assignedCustomers || []).find(
                      (c) => !customer || c.id !== customer.id
                    );
                    const isChecked = values.assignedEmployees.includes(employee.id);
                    const isBlocked = Boolean(conflict) && !isChecked;
                    return (
                      <EmployeeCheckboxLabel
                        key={employee.id}
                        $disabled={isBlocked}
                        title={isBlocked ? `Already assigned to ${conflict.name}` : undefined}
                      >
                        <input
                          type="checkbox"
                          disabled={isBlocked}
                          checked={isChecked}
                          onChange={() => {
                            const currentAssigned = [...values.assignedEmployees];

                            if (currentAssigned.includes(employee.id)) {
                              // Remove employee
                              const newAssigned = currentAssigned.filter(id => id !== employee.id);
                              setFieldValue('assignedEmployees', newAssigned);
                            } else {
                              // Add employee
                              setFieldValue('assignedEmployees', [...currentAssigned, employee.id]);
                            }
                          }}
                        />
                        {employee.firstName} {employee.lastName}
                        {isBlocked && ` (assigned to ${conflict.name})`}
                      </EmployeeCheckboxLabel>
                    );
                  })
                ) : (
                  <Hint>No active employees available to assign</Hint>
                )}
              </EmployeeList>
              </>}
            </AssignedEmployeesSection>

            {submitError && (
              <ErrorMessage role="alert">{submitError}</ErrorMessage>
            )}

            <FormActions>
              <Button 
                type="button" 
                variant="outline" 
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
              >
                {isSubmitting 
                  ? isEditing ? 'Updating...' : 'Creating...' 
                  : isEditing ? 'Update Customer' : 'Create Customer'}
              </Button>
            </FormActions>
          </Form>
        )}
      </Formik>
    </FormContainer>
  );
};

export default CustomerForm;
